import { useState, useEffect } from 'react';
import { useAuth } from '../state/useAuth';
import { useAuthModal } from '../state/AuthModalContext';
import { Mail, Lock, User, X, ArrowRight, UserPlus, LogIn, ShieldCheck } from 'lucide-react';

export default function AuthModal() {
  const { open, mode: initialMode, closeAuth } = useAuthModal();
  const { login, register } = useAuth();
  
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [form, setForm] = useState({
    identity: '', email: '', name: '', password: '', passwordConfirm: ''
  });

  // Sync mode with context when opened
  useEffect(() => { if (open) { setMode(initialMode || 'login'); setError(''); } }, [open, initialMode]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      if (mode === 'login') {
        await login(form.identity, form.password);
      } else {
        if (form.password !== form.passwordConfirm) throw new Error('Les mots de passe ne correspondent pas');
        await register({ email: form.email, name: form.name, password: form.password });
        await login(form.email, form.password); // Auto-login after register
      }
      closeAuth();
    } catch (err) {
      setError(err?.message || (mode === 'login' ? 'Identifiants incorrects' : 'Erreur lors de l\'inscription'));
    } finally { setLoading(false); }
  }

  return (
    <div className="auth-backdrop">
      <div className="auth-modal-container">
        
        {/* DESIGN ELEMENTS */}
        <div className="auth-decor-1"></div>
        <div className="auth-decor-2"></div>
        
        <div className="auth-card">
          <button onClick={closeAuth} className="auth-close-btn" aria-label="Fermer"><X size={20} /></button>
          
          <div className="auth-header">
            <div className="auth-logo-symbol">AEF</div>
            <h2>{mode === 'login' ? 'Bon retour parmi nous !' : 'Rejoindre AEF 32'}</h2>
            <p className="auth-subtitle">
              {mode === 'login' ? 'Accédez à votre espace sécurisé' : 'Créez votre profil en quelques secondes'}
            </p>
          </div>

          <div className="auth-tabs">
            <button className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>Connexion</button>
            <button className={mode === 'register' ? 'active' : ''} onClick={() => setMode('register')}>Inscription</button>
            <div className={`auth-tab-glider ${mode}`}></div>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="auth-error">{error}</div>}
            
            <div className="auth-inputs">
              {mode === 'login' ? (
                <div className="auth-field">
                  <span className="auth-field-icon"><Mail size={18}/></span>
                  <input required placeholder="Email ou Username" value={form.identity} onChange={e => setForm({...form, identity: e.target.value})} />
                </div>
              ) : (
                <>
                  <div className="auth-field">
                    <span className="auth-field-icon"><User size={18}/></span>
                    <input required placeholder="Nom complet" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                  </div>
                  <div className="auth-field">
                    <span className="auth-field-icon"><Mail size={18}/></span>
                    <input required type="email" placeholder="Adresse Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                  </div>
                </>
              )}

              <div className="auth-field">
                <span className="auth-field-icon"><Lock size={18}/></span>
                <input required type="password" placeholder="Mot de passe" value={form.password} onChange={e => setForm({...form, password: e.target.value})} />
              </div>

              {mode === 'register' && (
                <div className="auth-field">
                  <span className="auth-field-icon"><ShieldCheck size={18}/></span>
                  <input required type="password" placeholder="Confirmer le mot de passe" value={form.passwordConfirm} onChange={e => setForm({...form, passwordConfirm: e.target.value})} />
                </div>
              )}
            </div>

            <button className="auth-submit-btn" disabled={loading} type="submit">
              {loading ? 'Traitement...' : (
                <>
                  <span>{mode === 'login' ? 'Se connecter' : 'C\'est parti !'}</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="auth-footer">
            {mode === 'login' ? (
              <span>Nouveau ici ? <button className="link" onClick={() => setMode('register')}>Créer un compte</button></span>
            ) : (
              <span>Déjà client ? <button className="link" onClick={() => setMode('login')}>Se connecter</button></span>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .auth-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 9999; animation: authFadeIn 0.3s ease; }
        .auth-modal-container { position: relative; width: min(460px, 92vw); padding: 5px; }
        
        .auth-card { 
          position: relative; background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(20px); border-radius: 32px; padding: 40px; border: 1px solid rgba(255,255,255, 0.5); box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); overflow: hidden;
        }

        /* DECORATIONS */
        .auth-decor-1 { position: absolute; top: -50px; right: -50px; width: 150px; height: 150px; background: radial-gradient(circle, rgba(234, 179, 8, 0.2) 0%, transparent 70%); border-radius: 50%; z-index: -1; filter: blur(20px); }
        .auth-decor-2 { position: absolute; bottom: -50px; left: -50px; width: 150px; height: 150px; background: radial-gradient(circle, rgba(15, 23, 42, 0.1) 0%, transparent 70%); border-radius: 50%; z-index: -1; filter: blur(20px); }

        .auth-header { text-align: center; margin-bottom: 30px; }
        .auth-logo-symbol { width: 50px; height: 50px; background: #0f172a; color: white; display: flex; align-items: center; justify-content: center; border-radius: 12px; margin: 0 auto 15px; font-weight: 900; letter-spacing: -1px; box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.4); }
        .auth-header h2 { font-size: 24px; font-weight: 800; color: #0f172a; margin-bottom: 5px; }
        .auth-subtitle { color: #64748b; font-size: 14px; margin: 0; }

        .auth-close-btn { position: absolute; top: 20px; right: 20px; background: #f1f5f9; border: none; width: 32px; height: 32px; border-radius: 16px; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
        .auth-close-btn:hover { background: #fee2e2; color: #ef4444; transform: rotate(90deg); }

        /* TABS / SWITCHER */
        .auth-tabs { position: relative; display: flex; background: #f1f5f9; padding: 4px; border-radius: 14px; margin-bottom: 30px; }
        .auth-tabs button { flex: 1; position: relative; z-index: 1; background: none; border: none; padding: 10px; font-weight: 700; font-size: 14px; color: #64748b; cursor: pointer; transition: color 0.3s; }
        .auth-tabs button.active { color: #0f172a; }
        .auth-tab-glider { position: absolute; top: 4px; bottom: 4px; width: calc(50% - 4px); background: #fff; border-radius: 10px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .auth-tab-glider.login { transform: translateX(0); }
        .auth-tab-glider.register { transform: translateX(100%); }

        /* FORM ELEMENTS */
        .auth-form { display: grid; gap: 20px; }
        .auth-error { padding: 12px; background: #fee2e2; color: #ef4444; border-radius: 12px; font-size: 13px; font-weight: 600; text-align: center; border: 1px solid #fecaca; }
        .auth-inputs { display: grid; gap: 12px; }
        .auth-field { position: relative; }
        .auth-field-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }
        .auth-field input { width: 100%; padding: 14px 14px 14px 45px; background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; font-size: 15px; outline: none; transition: border-color 0.2s; box-sizing: border-box; }
        .auth-field input:focus { border-color: #0f172a; box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.05); }

        .auth-submit-btn { width: 100%; padding: 14px; background: #0f172a; color: white; border: none; border-radius: 14px; font-weight: 700; font-size: 16px; display: flex; align-items: center; justify-content: center; gap: 10px; cursor: pointer; transition: all 0.2s; box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.3); }
        .auth-submit-btn:hover { background: #1e293b; transform: translateY(-1px); }
        .auth-submit-btn:disabled { background: #94a3b8; cursor: not-allowed; transform: none; }

        .auth-footer { text-align: center; font-size: 14px; color: #64748b; margin-top: 25px; }
        .auth-footer button.link { background: none; border: none; color: #0f172a; font-weight: 800; cursor: pointer; text-decoration: underline; padding: 0 5px; }

        @keyframes authFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}
